import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Card } from "../ui/card";
import { useTheme } from "../../contexts/ThemeContext";

interface CylinderMimicProps {
  tag: any;
  userRole?: string;
}

export function CylinderMimic({ tag, userRole }: CylinderMimicProps) {
  const { theme } = useTheme();
  const dk = theme === "dark";

  const hasValue = tag.value !== undefined && tag.value !== null;
  const val = Number(tag.value);
  const engMin = Number(tag.engMin ?? 0);
  const engMax = Number(tag.engMax ?? 100);

  let pct = 0;
  if (hasValue) {
    if (engMax !== engMin) {
      pct = ((val - engMin) / (engMax - engMin)) * 100;
    } else {
      pct = val;
    }
    pct = Math.max(0, Math.min(100, pct));
  }

  return (
    <Card className={`relative overflow-hidden border transition-all duration-300 hover:shadow-xl flex flex-col items-center p-6 ${hasValue
        ? dk
          ? "border-blue-500/50 bg-gradient-to-br from-slate-800 to-slate-900 shadow-lg shadow-blue-900/20"
          : "border-blue-400 bg-gradient-to-br from-blue-50 via-white to-sky-50/50 shadow-sm"
        : dk
          ? "border-slate-600/50 bg-gradient-to-br from-slate-800 to-slate-900"
          : "border-slate-200 bg-gradient-to-br from-slate-50 to-white"
      }`}>
      
      {/* Header */}
      <div className="w-full flex items-start justify-between mb-4">
        <div>
          <h3 className="font-bold text-xl leading-tight truncate" title={tag.name}>{tag.name}</h3>
          <p className={`text-sm mt-1 truncate ${dk ? "text-slate-400" : "text-slate-500"}`}>
            {hasValue ? `${val.toFixed(tag.scaling ? 2 : 1)} ${tag.unit || ''}` : "Disconnected"}
          </p>
        </div>
      </div>

      {/* Cylinder Visual */}
      <div className="relative mt-4 mb-4 flex flex-col items-center justify-center w-full">
        
        {/* Tank Valve / Neck Assembly */}
        <div className="flex flex-col items-center z-20 relative top-[2px]">
          {/* Top Valve Handle */}
          <div className={`w-8 h-2 rounded-full mb-1 ${dk ? "bg-slate-500" : "bg-slate-400"} shadow-md`} />
          <div className={`w-3 h-4 ${dk ? "bg-slate-600" : "bg-slate-300"} shadow-inner`} />
          {/* Neck Base */}
          <div className={`w-12 h-6 rounded-t-lg ${dk ? "bg-gradient-to-b from-slate-500 to-slate-700" : "bg-gradient-to-b from-slate-300 to-slate-400"} shadow-[inset_0_2px_4px_rgba(255,255,255,0.4)] border-b-2 border-slate-900/20`} />
        </div>

        {/* Tank Body (Capsule) */}
        <div className="relative w-32 h-[220px] rounded-[60px] z-10 shadow-2xl">
          
          {/* Tank Background (Empty State) */}
          <div className={`absolute inset-0 rounded-[60px] border-[3px] overflow-hidden ${
            dk ? "border-slate-500/30 bg-slate-800" : "border-slate-300/50 bg-slate-200"
          }`}>
            
            {/* Liquid Fill */}
            <motion.div
              initial={{ height: 0 }}
              animate={{ height: `${pct}%` }}
              transition={{ type: "spring", stiffness: 45, damping: 15 }}
              className="absolute bottom-0 w-full overflow-hidden"
            >
              {/* Fill Gradient Base */}
              <div className={`w-full h-full opacity-90 ${
                pct > 90 ? "bg-gradient-to-t from-red-600 via-red-500 to-red-400" :
                pct < 10 ? "bg-gradient-to-t from-yellow-600 via-yellow-500 to-yellow-400" :
                "bg-gradient-to-t from-cyan-700 via-cyan-500 to-cyan-400"
              }`} />
            </motion.div>
          </div>

          {/* 3D Glass & Metal Overlays (Pointer Events None so it stays on top) */}
          <div className="absolute inset-0 rounded-[60px] pointer-events-none shadow-[inset_-15px_0_25px_rgba(0,0,0,0.5),inset_10px_0_20px_rgba(255,255,255,0.4)]" />
          
          {/* Main Specular Highlight (The shiny reflection line) */}
          <div className="absolute left-[12%] top-[3%] bottom-[3%] w-[10%] bg-gradient-to-b from-white/0 via-white/60 to-white/0 rounded-full blur-[2px] pointer-events-none" />
          
          {/* Subtle Right Edge Reflection */}
          <div className="absolute right-[4%] top-[10%] bottom-[10%] w-[3%] bg-gradient-to-b from-white/0 via-white/20 to-white/0 rounded-full blur-[1px] pointer-events-none" />

          {/* Tick marks on the tank for aesthetics */}
          <div className="absolute left-1/2 -translate-x-1/2 top-0 bottom-0 w-full h-full pointer-events-none flex flex-col justify-between py-[15%] opacity-20">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="w-[20%] mx-auto h-[2px] bg-slate-900 mix-blend-overlay" />
            ))}
          </div>

        </div>
      </div>

      {/* Percentage Readout */}
      <div className={`text-2xl font-black mt-2 tracking-tight ${
        pct > 90 ? "text-red-500" : pct < 10 ? "text-yellow-500" : dk ? "text-cyan-400" : "text-cyan-600"
      }`}>
        {hasValue ? `${pct.toFixed(1)}%` : "---"}
      </div>
      <p className={`text-[10px] uppercase font-bold tracking-wider mt-1 ${dk ? "text-slate-500" : "text-slate-400"}`}>
        Fill Level
      </p>

    </Card>
  );
}
